import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Thermometer, Droplets, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RoomDetailModal = ({ room, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    temperature: room.temperature || '',
    water_amount_liters: room.water_amount_liters || '',
    status: room.status || 'active'
  });
  const [updating, setUpdating] = useState(false);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);

    try {
      const token = localStorage.getItem('token');
      const updateData = {};
      
      if (formData.temperature !== '') updateData.temperature = parseFloat(formData.temperature);
      if (formData.humidity !== '') updateData.humidity = parseFloat(formData.humidity);
      if (formData.watering_schedule) updateData.watering_schedule = formData.watering_schedule;
      updateData.status = formData.status;

      await axios.put(`${API}/rooms/${room.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Room updated successfully');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update room');
    } finally {
      setUpdating(false);
    }
  };

  const handleWatered = async () => {
    setUpdating(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/rooms/${room.id}`, {
        last_watered: new Date().toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Watering logged successfully');
      onUpdate();
      onClose();
    } catch (error) {
      toast.error('Failed to log watering');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="room-detail-modal">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold flex items-center gap-2">
            {room.name}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleUpdate} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="temperature" className="flex items-center gap-2">
                <Thermometer className="w-4 h-4" />
                Avg Temperature (°C)
              </Label>
              <Input
                id="temperature"
                data-testid="temperature-input"
                type="number"
                step="0.1"
                value={formData.temperature}
                onChange={(e) => setFormData({ ...formData, temperature: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="water_amount" className="flex items-center gap-2">
                <Droplets className="w-4 h-4" />
                Water Amount (L)
              </Label>
              <Input
                id="water_amount"
                data-testid="water-amount-input"
                type="number"
                step="0.1"
                value={formData.water_amount_liters}
                onChange={(e) => setFormData({ ...formData, water_amount_liters: e.target.value })}
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="status">Status</Label>
            <select
              id="status"
              data-testid="status-select"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg bg-white"
            >
              <option value="active">Active</option>
              <option value="idle">Idle</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>

          {room.last_watered && (
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="w-4 h-4" />
                <span>Last watered: {format(new Date(room.last_watered), 'PPp')}</span>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              data-testid="mark-watered-button"
              onClick={handleWatered}
              disabled={updating}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              Mark as Watered Now
            </Button>
            <Button
              type="submit"
              data-testid="update-room-button"
              disabled={updating}
              className="flex-1 bg-[#1A4D2E] hover:bg-[#143D23] text-white"
            >
              Update Room
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RoomDetailModal;